import type { SlateBlock } from '@atlas/renderer';
import { Transforms, Element, Editor } from 'slate';
import deleteSelection from '@atlas/tool/box/slate/deleteSelection';
import extractBlocksFromHtml from '@atlas/tool/box/slate/extractBlocksFromHtml';
import extractLeavesFromHtml from '@atlas/tool/box/slate/extractLeavesFromHtml';
import getNode from '@atlas/tool/box/slate/getNode';
import matchNode from '@atlas/tool/box/slate/matchNode';
import isTextBlock from '@atlas/tool/box/slate/isTextBlock';
import isListBlock, { type ListBlock } from '@atlas/tool/box/slate/isListBlock';
import reuploadFile from '@atlas/tool/box/file/reuploadFile';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function withRichTextInsertion(editor: Editor, options?: TWithRichTextInsertionOptions) {
    const { insertData } = editor;

    editor.insertData = (data) => {
        if (!editor.selection) return insertData(data);

        const cursorPoint = editor.selection.focus;

        const node = getNode<SlateBlock>(editor, [cursorPoint.path[0]]);
        if (!node) return insertData(data);

        deleteSelection(editor, editor.selection);

        const currentNode = getNode<SlateBlock>(editor, [cursorPoint.path[0]]);

        if (!currentNode) return insertData(data);

        if (currentNode.type === 'code-block') {
            const plain = data.getData('text/plain');
            Transforms.insertText(editor, plain);
            return;
        }

        const html = (data.types.includes('text/html') && data.getData('text/html')) || '';

        if (data.types.includes('text/plain') && (!html || data.types.includes('vscode-editor-data'))) {
            insertData(data);
            return;
        }

        const destinationBlock = matchNode<SlateBlock>(editor, Element.isElement);
        if (!destinationBlock) return;

        if (destinationBlock.type === 'table-cell') {
            const leaves = extractLeavesFromHtml(html);
            if (!leaves.length) return;
            Transforms.insertNodes(editor, leaves);
            return;
        }

        const blocks = extractBlocksFromHtml(html).filter((block) => {
            if (!options?.supportInlineImages && block.type === 'image') return false;
            return true;
        });
        if (!blocks.length) return;

        blocks.forEach((block) => {
            if (block.type !== 'image') return;

            const fingerprint = Math.random().toString();
            reuploadFile(block.src)
                .then((src) => {
                    const imageIndex = editor.children.findIndex(
                        (node) => Element.isElement(node) && node.type === 'image' && node.fingerprint === fingerprint,
                    );
                    if (imageIndex === -1) return;
                    Transforms.setNodes(editor, { src, fingerprint: undefined }, { at: [imageIndex] });
                })
                .catch(() => {
                    const imageIndex = editor.children.findIndex(
                        (node) => Element.isElement(node) && node.type === 'image' && node.fingerprint === fingerprint,
                    );
                    if (imageIndex === -1) return;
                    Transforms.removeNodes(editor, { at: [imageIndex] });
                });
            Object.assign(block, { src: '', fingerprint });
        });

        if (blocks.length === 1 && blocks[0].type === 'paragraph') {
            Transforms.insertNodes(editor, blocks[0].children);
            return;
        }

        if (destinationBlock.type === 'list-item') {
            if (isListBlock(blocks[0])) {
                const list = blocks.shift() as ListBlock;
                const { children } = list;
                const first = children.shift();
                if (first) Transforms.insertNodes(editor, first.children, { mode: 'lowest' });
                if (children.length) Transforms.insertNodes(editor, children);
            }

            Transforms.insertNodes(editor, blocks, { mode: 'highest' });
            return;
        }

        if (isTextBlock(currentNode) && currentNode.children.length === 1 && currentNode.children[0].text === '') {
            Transforms.removeNodes(editor, { at: [cursorPoint.path[0]] });
        }

        const blockIndex = cursorPoint.offset === 0 ? cursorPoint.path[0] : cursorPoint.path[0] + 1;

        Transforms.insertNodes(editor, blocks, { at: [blockIndex] });

        const position = Editor.end(editor, [blockIndex + blocks.length - 1]);
        changeEditorSelection(editor, { anchor: position, focus: position });
    };

    return editor;
}

// #region Types

type TWithRichTextInsertionOptions = {
    supportInlineImages?: boolean;
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('withRichTextInsertion', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
